import {
  type AddSeatsResponseDto,
  type ModifySeatsResponseDto,
  SeatsApi,
  type SeatsResponseDto,
} from '@saaga/billingv2-sdk'
import { useCallback } from 'react'

import { parseBillingError } from '@/utils/billing-error.util'

import { useBillingConfig } from './use-billing-config.hook'

export interface UseSeatsApiReturn {
  getSeats: () => Promise<SeatsResponseDto>
  addSeats: (quantity: number) => Promise<AddSeatsResponseDto>
  removeSeats: (quantity: number) => Promise<ModifySeatsResponseDto>
}

export const useSeatsApi = (): UseSeatsApiReturn => {
  const { createConfig } = useBillingConfig()

  const getSeatsClient = useCallback(async (): Promise<SeatsApi> => {
    const config = await createConfig()
    return new SeatsApi(config)
  }, [createConfig])

  const getSeats = useCallback(async (): Promise<SeatsResponseDto> => {
    try {
      const client = await getSeatsClient()
      return await client.seatsControllerGetSeats()
    } catch (err) {
      throw new Error(await parseBillingError(err))
    }
  }, [getSeatsClient])

  const addSeats = useCallback(
    async (quantity: number): Promise<AddSeatsResponseDto> => {
      try {
        const client = await getSeatsClient()
        const currentUrl = window.location.href
        return await client.seatsControllerAddSeats({
          addSeatsDto: {
            quantity,
            successUrl: currentUrl,
            cancelUrl: currentUrl,
          },
        })
      } catch (err) {
        throw new Error(await parseBillingError(err))
      }
    },
    [getSeatsClient]
  )

  const removeSeats = useCallback(
    async (quantity: number): Promise<ModifySeatsResponseDto> => {
      try {
        const client = await getSeatsClient()
        return await client.seatsControllerRemoveSeats({
          removeSeatsDto: { quantity },
        })
      } catch (err) {
        throw new Error(await parseBillingError(err))
      }
    },
    [getSeatsClient]
  )

  return { getSeats, addSeats, removeSeats }
}
